import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { MainLayout } from '@/components/layout/MainLayout'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { CamerasPage } from '@/pages/CamerasPage'
import { EventsPage } from '@/pages/EventsPage'
import { FavouritesPage } from '@/pages/FavouritesPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { GroupsPage } from '@/pages/GroupsPage'
import { UsersPage } from '@/pages/UsersPage'
import { TestCamerasPage } from '@/pages/TestCamerasPage'
import { AnnotationsPage } from '@/pages/AnnotationsPage'
import { CameraLogsPage } from '@/pages/CameraLogsPage'
import { G100LogsPage } from '@/pages/G100LogsPage'
import { G100ConsolePage } from '@/pages/G100ConsolePage'
import { SimMismatchesPage } from '@/pages/SimMismatchesPage'
import { AllCamerasPage } from '@/pages/AllCamerasPage'
import DetectionAccuracyPage from '@/pages/DetectionAccuracyPage'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { Loader2 } from 'lucide-react'

// Loading screen during hydration
function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  )
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, _hasHydrated } = useAuthStore()

  // Wait for hydration before checking auth
  if (!_hasHydrated) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Superuser-only route — redirects to dashboard if not superuser
function SuperuserRoute({ children }: { children: React.ReactNode }) {
  const { user, _hasHydrated } = useAuthStore()

  if (!_hasHydrated) return <LoadingScreen />
  if (!user?.is_superuser) return <Navigate to="/" replace />

  return <>{children}</>
}

// Public Route wrapper - redirects to dashboard if already authenticated
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, _hasHydrated } = useAuthStore()

  // Wait for hydration before checking auth
  if (!_hasHydrated) {
    return <LoadingScreen />
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Placeholder pages for routes not yet implemented
function PlaceholderPage({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation('common')
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold">{t(titleKey, { defaultValue: titleKey })}</h2>
        <p className="text-muted-foreground">{t('placeholder.comingSoon', { defaultValue: 'Coming soon...' })}</p>
      </div>
    </div>
  )
}

function App() {
  const { theme } = useUIStore()

  return (
    <>
      <Toaster
        position="top-right"
        expand={false}
        richColors
        closeButton
        duration={4500}
        gap={8}
        visibleToasts={3}
        offset={16}
        theme={theme === 'system' ? 'system' : theme}
      />
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="cameras" element={<CamerasPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="favourites" element={<FavouritesPage />} />
        <Route path="media" element={<PlaceholderPage titleKey="nav.media" />} />
        <Route path="notifications" element={<PlaceholderPage titleKey="nav.notifications" />} />
        <Route path="users" element={<SuperuserRoute><UsersPage /></SuperuserRoute>} />
        <Route path="test-cameras" element={<SuperuserRoute><TestCamerasPage /></SuperuserRoute>} />
        <Route path="annotations" element={<SuperuserRoute><AnnotationsPage /></SuperuserRoute>} />
        <Route path="camera-logs" element={<SuperuserRoute><CameraLogsPage /></SuperuserRoute>} />
        <Route path="g100-logs" element={<SuperuserRoute><G100LogsPage /></SuperuserRoute>} />
        <Route path="sim-mismatches" element={<SuperuserRoute><SimMismatchesPage /></SuperuserRoute>} />
        <Route path="all-cameras" element={<SuperuserRoute><AllCamerasPage /></SuperuserRoute>} />
        <Route path="detection-accuracy" element={<SuperuserRoute><DetectionAccuracyPage /></SuperuserRoute>} />
        <Route path="groups" element={<GroupsPage />} />
        {/* #309 — stage only: the page leaves for "/" when its API answers 404 */}
        <Route path="g100-console" element={<G100ConsolePage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}

export default App
